<template>
    <main>
        <!-- Hero section -->
        <div class="relative mt-2">
            <div class="absolute inset-x-0 bottom-0 h-1/2 bg-gray-100" />
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div class="relative shadow-xl sm:rounded-2xl sm:overflow-hidden">
                    <div class="absolute inset-0">
                        <picture>
                            <source srcset="/img/landing/classroom.avif" type="image/avif" />
                            <img src="/img/landing/classroom.jpg" alt="classroom" class="h-full w-full object-cover" />
                        </picture>
                        <div
                            class="absolute inset-0 bg-gradient-to-r from-purple-800 to-indigo-700"
                            style="mix-blend-mode: multiply"
                        />
                    </div>
                    <div class="relative px-4 py-16 sm:px-6 sm:py-24 lg:py-32 lg:px-8">
                        <h1 class="text-center text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                            <span class="block text-white">{{ $t('marketing.section1.main_title') }}</span>
                            <span class="block text-indigo-200">{{ $t('marketing.section1.subtitle') }}</span>
                        </h1>
                        <p class="mt-6 max-w-lg mx-auto text-center text-xl text-indigo-200 sm:max-w-3xl">
                            {{ $t('marketing.section1.presentation_text') }}
                        </p>
                        <p class="flex md:hidden justify-center mt-6 mx-auto sm:max-w-3xl">
                            <SocialLinks />
                        </p>
                        <div class="mt-10 max-w-sm mx-auto sm:max-w-none sm:flex sm:justify-center hidden sm:hidden">
                            <div class="space-y-4 sm:space-y-0 sm:mx-auto sm:inline-grid sm:grid-cols-2 sm:gap-5">
                                <a
                                    href="#"
                                    class="flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-indigo-700 bg-white hover:bg-indigo-50 sm:px-8"
                                >
                                    Get started
                                </a>
                                <a
                                    href="#"
                                    class="flex items-center justify-center px-4 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-500 bg-opacity-60 hover:bg-opacity-70 sm:px-8"
                                >
                                    Live demo
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Alternating Feature Sections -->
        <div class="relative pt-16 pb-32 lg:overflow-hidden">
            <div aria-hidden="true" class="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-gray-100" />
            <div class="relative">
                <div class="lg:mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-2 lg:grid-flow-col-dense lg:gap-24">
                    <div class="px-4 max-w-xl mx-auto sm:px-6 lg:py-16 lg:max-w-none lg:mx-0 lg:px-0">
                        <div>
                            <div>
                                <span
                                    class="h-12 w-12 rounded-md flex items-center justify-center bg-gradient-to-r from-purple-600 to-indigo-600"
                                >
                                    <!-- Heroicon name: outline/inbox -->
                                    <svg
                                        class="h-6 w-6 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                                        />
                                    </svg>
                                </span>
                            </div>
                            <div class="mt-6">
                                <h2 class="text-3xl font-extrabold tracking-tight text-gray-900">
                                    {{ $t('marketing.section2.title') }}
                                </h2>
                                <p class="mt-4 text-lg text-gray-500">
                                    {{ $t('marketing.section2.text') }}
                                </p>
                                <div class="mt-6 hidden">
                                    <a
                                        href="#"
                                        class="inline-flex px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                                    >
                                        Get started
                                    </a>
                                </div>
                            </div>
                        </div>
                        <div class="mt-8 border-t border-gray-200 pt-6">
                            <blockquote>
                                <div>
                                    <p class="text-base text-gray-500">
                                        &ldquo;{{ $t('marketing.section2.quote_inaki') }}&rdquo;
                                    </p>
                                </div>
                                <footer class="mt-3">
                                    <div class="flex items-center space-x-3">
                                        <div class="shrink-0">
                                            <picture>
                                                <source srcset="/img/landing/inaki_48x48.avif" type="image/avif" />
                                                <img
                                                    src="/img/landing/inaki_48x48.jpg"
                                                    alt="Iñaki Ibargoyen"
                                                    class="h-6 w-6 rounded-full"
                                                />
                                            </picture>
                                        </div>
                                        <div class="text-base font-medium text-gray-700">
                                            {{ $t('marketing.section2.quote_tag_inaki') }}
                                        </div>
                                    </div>
                                </footer>
                            </blockquote>
                        </div>
                    </div>
                    <div class="mt-12 sm:mt-16 lg:mt-0">
                        <!--                        -mr-48-->
                        <div class="pl-4 sm:pl-6 md:-mr-16 lg:px-0 lg:m-0 lg:relative lg:h-full">
                            <img
                                class="w-full rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 lg:absolute lg:left-0 lg:h-full lg:w-auto lg:max-w-none"
                                src="/img/landing/screenshots/rollcall_es.png"
                                alt=""
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="mt-24">
                <div class="lg:mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-2 lg:grid-flow-col-dense lg:gap-24">
                    <div class="px-4 max-w-xl mx-auto sm:px-6 lg:py-32 lg:max-w-none lg:mx-0 lg:px-0 lg:col-start-2">
                        <div>
                            <div>
                                <span
                                    class="h-12 w-12 rounded-md flex items-center justify-center bg-gradient-to-r from-purple-600 to-indigo-600"
                                >
                                    <!-- Heroicon name: outline/sparkles -->
                                    <svg
                                        class="h-6 w-6 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        aria-hidden="true"
                                    >
                                        <path
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            stroke-width="2"
                                            d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                                        />
                                    </svg>
                                </span>
                            </div>
                            <div class="mt-6">
                                <h2 class="text-3xl font-extrabold tracking-tight text-gray-900">
                                    {{ $t('marketing.section3.title') }}
                                </h2>
                                <p class="mt-4 text-lg text-gray-500">
                                    {{ $t('marketing.section3.text') }}
                                </p>
                                <div class="mt-6 hidden">
                                    <a
                                        href="#"
                                        class="inline-flex px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                                    >
                                        Get started
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mt-12 sm:mt-16 lg:mt-0 lg:col-start-1">
                        <div class="pr-4 -ml-48 sm:pr-6 md:-ml-16 lg:px-0 lg:m-0 lg:relative lg:h-full">
                            <img
                                class="w-full rounded-xl shadow-xl ring-1 ring-black ring-opacity-5 lg:absolute lg:right-0 lg:h-full lg:w-auto lg:max-w-none"
                                src="/img/landing/screenshots/grading_es.png"
                                alt="Customer profile user interface"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Feature section with screenshot -->
        <div class="relative bg-gray-50 pt-16 sm:pt-24 lg:pt-32">
            <div class="mx-auto max-w-md px-4 text-center sm:px-6 sm:max-w-3xl lg:px-8 lg:max-w-7xl">
                <div>
                    <h2 class="text-base font-semibold tracking-wider text-indigo-600 uppercase">
                        {{ $t('marketing.section4.title') }}
                    </h2>
                    <p class="mt-2 text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
                        {{ $t('marketing.section4.subtitle') }}
                    </p>
                    <p class="mt-5 max-w-prose mx-auto text-xl text-gray-500">
                        {{ $t('marketing.section4.text') }}
                    </p>
                </div>
                <div class="mt-12 -mb-10 sm:-mb-24 lg:-mb-80">
                    <img
                        class="rounded-lg shadow-xl ring-1 ring-black ring-opacity-5"
                        src="/img/landing/screenshots/genius_es.png"
                        alt=""
                    />
                    <!--              <img class="rounded-lg shadow-xl ring-1 ring-black ring-opacity-5" src="https://tailwindui.com/img/landing/component-images/green-project-app-screenshot.jpg" alt="">-->
                </div>
            </div>
        </div>

        <!-- Feature section with screenshot -->
        <div class="relative bg-gray-50 pt-16 sm:pt-24 lg:pt-32" v-if="false">
            <div class="mx-auto max-w-md px-4 text-center sm:px-6 sm:max-w-3xl lg:px-8 lg:max-w-7xl">
                <div>
                    <p class="mt-2 text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
                        {{ $t('marketing.section4b.subtitle') }}
                    </p>
                    <p class="mt-5 max-w-prose mx-auto text-xl text-gray-500">
                        {{ $t('marketing.section4b.text') }}
                    </p>
                </div>
                <div>
                    <video autoplay muted loop>
                        <source src="/img/landing/compare_grades_between_schools.mp4" type="video/mp4" />
                    </video>
                </div>
            </div>
        </div>

        <!-- Feature section with grid -->

        <!-- Testimonial section -->

        <!-- Blog section -->
        <div class="relative bg-gray-50 py-16 sm:py-24 lg:py-32">
            <div class="relative">
                <div class="text-center mx-auto max-w-md px-4 sm:max-w-3xl sm:px-6 lg:px-8 lg:max-w-7xl">
                    <h2 class="text-base font-semibold tracking-wider text-cyan-600 uppercase">
                        {{ $t('marketing.section5.options') }}
                    </h2>
                    <p class="mt-2 text-3xl font-extrabold text-gray-900 tracking-tight sm:text-4xl">
                        {{ $t('marketing.section5.options_title') }}
                    </p>
                    <p class="mt-5 mx-auto max-w-prose text-xl text-gray-500">
                        {{ $t('marketing.section5.options_text') }}
                    </p>
                </div>
                <div
                    class="mt-12 mx-auto max-w-md px-4 grid gap-8 sm:max-w-lg sm:px-6 lg:px-8 lg:grid-cols-3 lg:max-w-7xl"
                >
                    <div class="flex flex-col rounded-lg shadow-lg overflow-hidden">
                        <div class="shrink-0">
                            <img
                                class="h-48 w-full object-cover"
                                src="https://images.unsplash.com/photo-1525829528215-ffae12a76ac8?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=384&q=80"
                                alt=""
                            />
                        </div>
                        <div class="flex-1 bg-white p-6 flex flex-col justify-between">
                            <div class="flex-1">
                                <a href="#" class="block mt-2">
                                    <p class="text-xl font-semibold text-gray-900">
                                        {{ $t('marketing.section5.option1_title') }}
                                    </p>
                                    <p class="mt-3 text-base text-gray-500">
                                        {{ $t('marketing.section5.option1_text') }}
                                    </p>
                                </a>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col rounded-lg shadow-lg overflow-hidden">
                        <div class="shrink-0">
                            <img
                                class="h-48 w-full object-cover"
                                src="https://images.unsplash.com/photo-1584438784894-089d6a62b8fa?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=384&q=80"
                                alt=""
                            />
                        </div>
                        <div class="flex-1 bg-white p-6 flex flex-col justify-between">
                            <div class="flex-1">
                                <a href="#" class="block mt-2">
                                    <p class="text-xl font-semibold text-gray-900">
                                        {{ $t('marketing.section5.option2_title') }}
                                    </p>
                                    <p class="mt-3 text-base text-gray-500">
                                        {{ $t('marketing.section5.option2_text') }}
                                    </p>
                                </a>
                            </div>
                        </div>
                    </div>

                    <div class="flex flex-col rounded-lg shadow-lg overflow-hidden">
                        <div class="shrink-0">
                            <img
                                class="h-48 w-full object-cover"
                                src="https://images.unsplash.com/photo-1476703993599-0035a21b17a9?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=384&q=80"
                                alt=""
                            />
                        </div>
                        <div class="flex-1 bg-white p-6 flex flex-col justify-between">
                            <div class="flex-1">
                                <a href="#" class="block mt-2">
                                    <p class="text-xl font-semibold text-gray-900">
                                        {{ $t('marketing.section5.option3_title') }}
                                    </p>
                                    <p class="mt-3 text-base text-gray-500">
                                        {{ $t('marketing.section5.option3_text') }}
                                    </p>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="relative bg-white py-16 sm:py-24 lg:py-32">
            <div class="mx-auto max-w-md px-6 text-center sm:max-w-3xl lg:max-w-7xl lg:px-8">
                <h2 class="text-lg font-semibold text-indigo-600">{{ $t('marketing.section9.supertitle') }}</h2>
                <p class="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                    {{ $t('marketing.section9.title') }}
                </p>
                <p class="mx-auto mt-5 max-w-prose text-xl text-gray-500">
                    {{ $t('marketing.section9.description') }}
                </p>
                <div class="mt-12">
                    <div class="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                        <div class="pt-6">
                            <div class="flow-root rounded-lg bg-gray-50 px-6 pb-8">
                                <div class="-mt-6">
                                    <div>
                                        <span
                                            class="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-indigo-400 to-indigo-600 p-3 shadow-lg"
                                        >
                                            <IconBrandAppleArcade class="h-6 w-6 text-white" />
                                        </span>
                                    </div>
                                    <h3 class="mt-8 text-lg font-medium tracking-tight text-gray-900">
                                        {{ $t('marketing.section9.item_1_title') }}
                                    </h3>
                                    <p class="mt-5 text-base text-gray-500">
                                        {{ $t('marketing.section9.item_1_description') }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="pt-6">
                            <div class="flow-root rounded-lg bg-gray-50 px-6 pb-8">
                                <div class="-mt-6">
                                    <div>
                                        <span
                                            class="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-indigo-400 to-indigo-600 p-3 shadow-lg"
                                        >
                                            <IconBrain class="h-6 w-6 text-white" />
                                        </span>
                                    </div>
                                    <h3 class="mt-8 text-lg font-medium tracking-tight text-gray-900">
                                        {{ $t('marketing.section9.item_2_title') }}
                                    </h3>
                                    <p class="mt-5 text-base text-gray-500">
                                        {{ $t('marketing.section9.item_2_description') }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="pt-6">
                            <div class="flow-root rounded-lg bg-gray-50 px-6 pb-8">
                                <div class="-mt-6">
                                    <div>
                                        <span
                                            class="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-indigo-400 to-indigo-600 p-3 shadow-lg"
                                        >
                                            <IconBadge class="h-6 w-6 text-white" />
                                        </span>
                                    </div>
                                    <h3 class="mt-8 text-lg font-medium tracking-tight text-gray-900">
                                        {{ $t('marketing.section9.item_3_title') }}
                                    </h3>
                                    <p class="mt-5 text-base text-gray-500">
                                        {{ $t('marketing.section9.item_3_description') }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="pt-6">
                            <div class="flow-root rounded-lg bg-gray-50 px-6 pb-8">
                                <div class="-mt-6">
                                    <div>
                                        <span
                                            class="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-indigo-400 to-indigo-600 p-3 shadow-lg"
                                        >
                                            <IconCards class="h-6 w-6 text-white" />
                                        </span>
                                    </div>
                                    <h3 class="mt-8 text-lg font-medium tracking-tight text-gray-900">
                                        {{ $t('marketing.section9.item_4_title') }}
                                    </h3>
                                    <p class="mt-5 text-base text-gray-500">
                                        {{ $t('marketing.section9.item_4_description') }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="pt-6">
                            <div class="flow-root rounded-lg bg-gray-50 px-6 pb-8">
                                <div class="-mt-6">
                                    <div>
                                        <span
                                            class="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-indigo-400 to-indigo-600 p-3 shadow-lg"
                                        >
                                            <IconMap class="h-6 w-6 text-white" />
                                        </span>
                                    </div>
                                    <h3 class="mt-8 text-lg font-medium tracking-tight text-gray-900">
                                        {{ $t('marketing.section9.item_5_title') }}
                                    </h3>
                                    <p class="mt-5 text-base text-gray-500">
                                        {{ $t('marketing.section9.item_5_description') }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="pt-6">
                            <div class="flow-root rounded-lg bg-gray-50 px-6 pb-8">
                                <div class="-mt-6">
                                    <div>
                                        <span
                                            class="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-indigo-400 to-indigo-600 p-3 shadow-lg"
                                        >
                                            <IconHeartHandshake class="h-6 w-6 text-white" />
                                        </span>
                                    </div>
                                    <h3 class="mt-8 text-lg font-medium tracking-tight text-gray-900">
                                        {{ $t('marketing.section9.item_6_title') }}
                                    </h3>
                                    <p class="mt-5 text-base text-gray-500">
                                        {{ $t('marketing.section9.item_6_description') }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- This example requires Tailwind CSS v2.0+ -->
        <TestimonialSection
            :name="$t('marketing.section6.testimonial1')"
            :credentials="$t('marketing.section6.credentials1')"
            :quote="$t('marketing.section6.testimonial1_quote')"
            photo-jpg="/img/landing/bernardo_jareno_square_bw_320x320.jpg"
        />
        <TestimonialSection
            :name="$t('marketing.section6b.testimonial1')"
            :credentials="$t('marketing.section6b.credentials1')"
            :quote="$t('marketing.section6b.testimonial1_quote')"
            photo-jpg="/img/landing/carlos_negrin.jpg"
        />

        <!-- This example requires Tailwind CSS v2.0+ -->
        <LandingContactSection />

        <!-- Stats section -->
        <div class="relative bg-gray-900">
            <div class="h-80 absolute bottom-0 xl:inset-0 xl:h-auto xl:grid xl:grid-cols-2">
                <div class="h-full xl:relative xl:col-start-2">
                    <img
                        class="h-full w-full object-cover opacity-25 xl:absolute xl:inset-0"
                        src="https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?ixid=MXwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHw%3D&ixlib=rb-1.2.1&auto=format&fit=crop&w=950&q=80&sat=-100"
                        alt="People working on laptops"
                    />
                    <div
                        aria-hidden="true"
                        class="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-gray-900 xl:inset-y-0 xl:left-0 xl:h-full xl:w-32 xl:bg-gradient-to-r"
                    />
                </div>
            </div>
            <div
                class="max-w-4xl mx-auto px-4 sm:px-6 lg:max-w-7xl lg:px-8 xl:grid xl:grid-cols-2 xl:grid-flow-col-dense xl:gap-x-8"
            >
                <div class="relative pt-12 pb-64 sm:pt-24 sm:pb-64 xl:col-start-1 xl:pb-24">
                    <h2 class="text-sm font-semibold tracking-wide uppercase">
                        <span class="bg-gradient-to-r from-purple-300 to-indigo-300 bg-clip-text text-transparent">
                            {{ $t('marketing.section7.title') }}
                        </span>
                    </h2>
                    <p class="mt-3 text-3xl font-extrabold text-white">
                        {{ $t('marketing.section7.text') }}
                    </p>
                    <p class="mt-5 text-lg text-gray-300">
                        {{ $t('marketing.section7.text2') }}
                    </p>
                    <div class="mt-12 grid grid-cols-1 gap-y-12 gap-x-6 sm:grid-cols-2">
                        <p>
                            <span class="block text-2xl font-bold text-white">{{
                                $t('marketing.section7.metric1_title')
                            }}</span>
                            <span class="mt-1 block text-base text-gray-300">{{
                                $t('marketing.section7.metric1_text')
                            }}</span>
                        </p>

                        <p>
                            <span class="block text-2xl font-bold text-white">{{
                                $t('marketing.section7.metric2_title')
                            }}</span>
                            <span class="mt-1 block text-base text-gray-300">{{
                                $t('marketing.section7.metric2_text')
                            }}</span>
                        </p>

                        <p>
                            <span class="block text-2xl font-bold text-white">{{
                                $t('marketing.section7.metric3_title')
                            }}</span>
                            <span class="mt-1 block text-base text-gray-300">{{
                                $t('marketing.section7.metric3_text')
                            }}</span>
                        </p>

                        <p>
                            <span class="block text-2xl font-bold text-white">{{
                                $t('marketing.section7.metric4_title')
                            }}</span>
                            <span class="mt-1 block text-base text-gray-300">{{
                                $t('marketing.section7.metric4_text')
                            }}</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>

<script>
import LandingLayout from '@/Shared/LandingLayout.vue';
import TestimonialSection from '@/components/Landing/TestimonialSection.vue';
import SocialLinks from '@/components/Landing/SocialLinks.vue';
import { IconBadge, IconBrain, IconBrandAppleArcade, IconCards, IconHeartHandshake, IconMap } from '@tabler/icons-vue';
import LandingContactSection from './LandingContactSection.vue';

export default {
    props: {
        meta: Object,
    },
    components: {
        LandingContactSection,
        TestimonialSection,
        SocialLinks,
        IconBrain,
        IconCards,
        IconHeartHandshake,
        IconMap,
        IconBadge,
        IconBrandAppleArcade,
    },
    layout: LandingLayout,
    mounted() {
        this.$i18n.locale = this.meta.locale;
    },
};
</script>
